/* Source rail behavior adapted for safe section re-rendering. No cart writes. */
(() => {
  if (customElements.get('ew-home-rail')) return;
  class EdelweissHomeRail extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.track = this.querySelector('[data-rail-track]');
      this.dots = this.querySelector('[data-rail-dots]');
      if (!this.track || !this.dots) return;
      this.abort = new AbortController();
      const opts = {signal:this.abort.signal};
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.dots.addEventListener('click', e => {
        const dot = e.target.closest('[data-rail-position]');
        if (dot) this.go(Number(dot.dataset.railPosition));
      }, opts);
      this.track.addEventListener('scroll', () => this.update(), {...opts,passive:true});
      this.track.addEventListener('keydown', e => {
        if (!['ArrowLeft','ArrowRight'].includes(e.key)) return;
        e.preventDefault(); this.move(e.key === 'ArrowLeft' ? -1 : 1);
      }, opts);
      this.track.addEventListener('pointerdown', e => {
        if(e.pointerType !== 'mouse' || e.button !== 0) return;
        this.pointer = e.pointerId; this.startX=e.clientX;
        this.startScroll=this.track.scrollLeft;this.dragged=false;
      }, opts);
      this.track.addEventListener('pointermove', e => {
        if(e.pointerId !== this.pointer) return;
        if(!(e.buttons & 1)) {this.endDrag();return;}
        const distance=e.clientX-this.startX;
        if(Math.abs(distance)>5 && !this.dragged) {
          this.dragged=true;this.track.setPointerCapture(e.pointerId);
          this.track.classList.add('is-dragging');
        }
        if(this.dragged){e.preventDefault();this.track.scrollLeft=this.startScroll-distance;}
      }, opts);
      this.track.addEventListener('pointerup',()=>this.endDrag(),opts);
      this.track.addEventListener('pointercancel',()=>{this.endDrag();this.dragged=false;},opts);
      this.track.addEventListener('click',e=>{if(this.dragged){e.preventDefault();e.stopPropagation();this.dragged=false;}},{...opts,capture:true});
      this.track.addEventListener('dragstart',e=>e.preventDefault(),opts);
      if('ResizeObserver' in window){this.observer=new ResizeObserver(()=>this.update());this.observer.observe(this.track);}
      this.update();
    }
    endDrag(){if(this.pointer != null && this.track.hasPointerCapture(this.pointer))this.track.releasePointerCapture(this.pointer);this.pointer=null;this.track.classList.remove('is-dragging');}
    go(position) {
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      this.track.scrollTo({left:position*(rtl?-1:1),behavior:this.motion.matches?'auto':'smooth'});
    }
    move(dir) {
      const position = Math.abs(this.track.scrollLeft);
      const index = this.positions.reduce((best, p, i) => Math.abs(p-position)<Math.abs(this.positions[best]-position)?i:best,0);
      this.go(this.positions[Math.max(0,Math.min(this.positions.length-1,index+dir))]);
    }
    update() {
      const max = Math.max(0,this.track.scrollWidth-this.track.clientWidth);
      const cards = [...this.track.children];
      const first = cards[0]?.getBoundingClientRect();
      this.positions = [];
      cards.forEach(card => {
        const position = Math.min(max,Math.abs(card.getBoundingClientRect().left-first.left));
        if (!this.positions.length || position-this.positions.at(-1)>2) this.positions.push(position);
      });
      const signature = this.positions.join(',');
      if (signature !== this.dotSignature) {
        this.dotSignature = signature;
        this.dots.replaceChildren(...this.positions.map((position,index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'audit-home__rail-dot';
          dot.dataset.railPosition = position;
          dot.setAttribute('aria-controls',this.track.id);
          dot.setAttribute('aria-label',cards[index].querySelector('h3')?.textContent.trim() || String(index+1));
          return dot;
        }));
      }
      this.dots.hidden = max<=2;
      const position = Math.abs(this.track.scrollLeft);
      const active = this.positions.reduce((best,p,i) => Math.abs(p-position)<Math.abs(this.positions[best]-position)?i:best,0);
      [...this.dots.children].forEach((dot,index) => dot.setAttribute('aria-current',String(index===active)));
    }
    disconnectedCallback(){this.abort?.abort();this.observer?.disconnect();this.abort=null;}
  }
  customElements.define('ew-home-rail',EdelweissHomeRail);
})();

(() => {
  if (customElements.get('ew-home-hero')) return;
  class EdelweissHomeHero extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.copy = this.querySelector('.audit-home__hero-copy');
      this.image = this.querySelector('.audit-home__hero-media img');
      if (!this.copy) return;
      this.abort = new AbortController();
      const options = {signal:this.abort.signal};
      this.schedule = () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.update());
      };
      window.addEventListener('resize', this.schedule, options);
      window.visualViewport?.addEventListener('resize', this.schedule, options);
      this.image?.addEventListener('load', this.schedule, options);
      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(entries => {
          if (entries.some(entry => entry.target !== this || entry.contentRect.width !== this.width)) this.schedule();
        });
        this.observer.observe(this);
        this.observer.observe(this.copy);
        document.querySelectorAll('.shopify-section-group-header-group, .section-header').forEach(node => this.observer.observe(node));
      }
      document.fonts?.ready.then(() => { if (this.isConnected) this.schedule(); });
      this.update();
    }
    update() {
      if (!this.isConnected) return;
      const rect = this.getBoundingClientRect();
      this.width = rect.width;
      const viewport = window.visualViewport?.height || window.innerHeight;
      const top = Math.max(0, rect.top + window.scrollY);
      const gap = Math.min(32, Math.max(16, viewport * .03));
      const available = Math.max(0, viewport - top - 16);
      // Base the mode on the window, not content height, so resizing cannot oscillate.
      const shortWide = this.width >= 900 && available <= 480;
      this.classList.toggle('is-short-wide', shortWide);
      const ratio = this.image?.naturalWidth && this.image?.naturalHeight
        ? this.image.naturalWidth / this.image.naturalHeight
        : (Number(this.image?.getAttribute('width')) / Number(this.image?.getAttribute('height'))) || 1600 / 1050;
      let imageHeight;
      let heroHeight;
      if (shortWide) {
        const stageWidth = Math.min(1200, this.width - 2 * gap);
        const copyWidth = Math.min(520, (stageWidth - 32) * .46);
        // Establish the new column before measuring wrapped or enlarged text.
        this.style.setProperty('--ew-hero-copy-width', `${Math.round(copyWidth * 100) / 100}px`);
        imageHeight = Math.min(Math.max(available, 160), (stageWidth - 32 - copyWidth) / ratio);
        const copyHeight = this.copy.getBoundingClientRect().height;
        // In exceptionally short windows, let the section grow instead of clipping copy.
        heroHeight = Math.max(imageHeight, copyHeight + 2 * gap);
      } else {
        this.style.removeProperty('--ew-hero-copy-width');
        const copyHeight = this.copy.getBoundingClientRect().height;
        imageHeight = Math.max(0, Math.min(this.width / ratio, available, (available - copyHeight - gap) / .72));
        heroHeight = Math.max(imageHeight, imageHeight * .72 + copyHeight + gap, copyHeight + 2 * gap);
      }
      const values = {
        '--ew-hero-image-width': imageHeight * ratio,
        '--ew-hero-image-height': imageHeight,
        '--ew-hero-height': heroHeight,
        '--ew-hero-gap': gap
      };
      Object.entries(values).forEach(([name, value]) => {
        const pixels = `${Math.round(value * 100) / 100}px`;
        if (this.style.getPropertyValue(name) !== pixels) this.style.setProperty(name, pixels);
      });
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      this.abort = null;
    }
  }
  customElements.define('ew-home-hero', EdelweissHomeHero);
})();

